/**
 * Shared constants and utilities for delivery plan rendering.
 * Used by both ProjectDetail and Admin delivery profile editor.
 *
 * Skills and roles colours come from the database (vcf_skills + admin_roles tables).
 * Use the helper functions below with the loaded data — do NOT hardcode colours here.
 */

import type { ActivityPhase } from '@/hooks/useWorkstreams';

export const NUM_WEEKS_PROJECT = 44;
export const NUM_WEEKS_PROFILE = 12;
export const DAYS_PER_WEEK_DEFAULT = 5;

const DEFAULT_COLOURS = { bg: '#F3F4F6', text: '#374151' };

/**
 * Look up colours for a skill by name or ID from the loaded skills data.
 * Returns default grey if not found.
 */
export function getSkillColours(
  skillNameOrId: string | null | undefined,
  skills: Array<{ id: string; name: string; code: string; colorBg: string; colorText: string }> = [],
): { bg: string; text: string } {
  if (!skillNameOrId) return DEFAULT_COLOURS;
  const upper = skillNameOrId.toUpperCase();
  const found = skills.find(s =>
    s.id === skillNameOrId ||
    s.name.toUpperCase() === upper ||
    s.code.toUpperCase() === upper
  );
  if (found) return { bg: found.colorBg, text: found.colorText };
  return DEFAULT_COLOURS;
}

/**
 * Look up colours for a role by name from the loaded roles data.
 * Returns default grey if not found.
 */
export function getRoleColours(
  roleName: string | null | undefined,
  roles: Array<{ name: string; colorBg?: string | null; colorText?: string | null }> = [],
): { bg: string; text: string } {
  if (!roleName) return DEFAULT_COLOURS;
  const found = roles.find(r => r.name === roleName);
  if (found) return { bg: found.colorBg || DEFAULT_COLOURS.bg, text: found.colorText || DEFAULT_COLOURS.text };
  return DEFAULT_COLOURS;
}

// Phase colour mapping
export const PHASE_COLOURS: Record<ActivityPhase, { bg: string; text: string; label: string }> = {
  workshop: { bg: '#FEF3C7', text: '#92400E', label: 'WORKSHOP' },
  assess: { bg: '#DBEAFE', text: '#1E40AF', label: 'ASSESS' },
  design: { bg: '#E0E7FF', text: '#3730A3', label: 'DESIGN' },
  implement: { bg: '#D1FAE5', text: '#065F46', label: 'IMPLEMENT' },
  validate: { bg: '#FDE68A', text: '#92400E', label: 'VALIDATE' },
  document: { bg: '#FCE7F3', text: '#9D174D', label: 'DOCUMENT' },
  kt: { bg: '#F3E8FF', text: '#6B21A8', label: 'KT' },
};
